import React, { useState, useEffect } from 'react';
import { FileText, Eye, Edit2, X, ChevronDown, FolderOpen, Plus, Loader2, Link, Unlink, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Google Docs icon
const GoogleDocsIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z" fill="#4285F4"/>
    <path d="M8 12h8v1.5H8V12zm0 3h8v1.5H8V15zm0 3h5v1.5H8V18z" fill="#4285F4"/>
  </svg>
);

// Google Sheets icon
const GoogleSheetsIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z" fill="#0F9D58"/>
    <path d="M8 12h3v2H8v-2zm4 0h4v2h-4v-2zm-4 3h3v2H8v-2zm4 0h4v2h-4v-2z" fill="#0F9D58"/>
  </svg>
);

const GoogleDocsSection = ({ userId }) => {
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [googleFiles, setGoogleFiles] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleFolderId, setGoogleFolderId] = useState('root');
  const [googleFolderHistory, setGoogleFolderHistory] = useState([]);
  const [googleFileType, setGoogleFileType] = useState('all');
  const [showGoogleDocViewer, setShowGoogleDocViewer] = useState(false);
  const [selectedGoogleDoc, setSelectedGoogleDoc] = useState(null);
  const [googleViewMode, setGoogleViewMode] = useState('view');
  const [showCreateGoogleDocModal, setShowCreateGoogleDocModal] = useState(false);
  const [newGoogleDocName, setNewGoogleDocName] = useState('');
  const [newGoogleDocType, setNewGoogleDocType] = useState('doc');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Categories for organizing Google Docs
  const categories = [
    { id: 'all', label: 'All', color: 'bg-gray-600' },
    { id: 'recent', label: 'Recent', color: 'bg-blue-500' },
    { id: 'starred', label: 'Starred', color: 'bg-yellow-500' },
    { id: 'shared', label: 'Shared with me', color: 'bg-green-500' },
    { id: 'owned', label: 'My Documents', color: 'bg-purple-500' },
  ];

  useEffect(() => {
    if (userId) {
      checkGoogleConnection();
    }
  }, [userId]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_connected') === 'true') {
      setGoogleConnected(true);
      toast.success('Google Docs connected successfully!');
      window.history.replaceState({}, '', '/documents-re');
      if (userId) {
        fetchGoogleFiles();
      }
    }
  }, [userId]);

  useEffect(() => {
    if (googleConnected && userId) {
      fetchGoogleFiles(googleFolderId, googleFileType, selectedCategory);
    }
  }, [googleConnected, googleFileType, selectedCategory]);

  const checkGoogleConnection = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/google-docs/status?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        setGoogleConnected(data.connected);
        setGoogleEmail(data.email || '');
      }
    } catch (error) {
      console.error('Error checking Google connection:', error);
    }
  };

  const connectGoogleDocs = async () => {
    if (!userId) {
      toast.error('Please log in to connect Google Docs');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/google-docs/connect?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to connect to Google');
      }
    } catch (error) {
      toast.error('Failed to connect to Google Docs');
    }
  };

  const disconnectGoogleDocs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/google-docs/disconnect?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setGoogleConnected(false);
        setGoogleEmail('');
        setGoogleFiles([]);
        toast.success('Google Docs disconnected');
      }
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const fetchGoogleFiles = async (folderId = 'root', fileType = googleFileType, category = selectedCategory) => {
    if (!userId) return;
    setGoogleLoading(true);
    try {
      let url = `${API_URL}/api/google-docs/files?user_id=${encodeURIComponent(userId)}&folder_id=${folderId}&file_type=${fileType}`;
      if (category && category !== 'all') {
        url += `&category=${category}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setGoogleFiles(data.items || []);
        setGoogleFolderId(folderId);
      } else {
        toast.error('Failed to load Google files');
      }
    } catch (error) {
      console.error('Google files error:', error);
      toast.error('Failed to load Google files');
    } finally {
      setGoogleLoading(false);
    }
  };

  const navigateGoogleFolder = (folder) => {
    setGoogleFolderHistory(prev => [...prev, googleFolderId]);
    fetchGoogleFiles(folder.id);
  };

  const navigateGoogleBack = () => {
    if (googleFolderHistory.length === 0) return;
    const newHistory = [...googleFolderHistory];
    const previousFolder = newHistory.pop();
    setGoogleFolderHistory(newHistory);
    fetchGoogleFiles(previousFolder);
  };

  const openGoogleDoc = async (file, mode = 'view') => {
    try {
      const response = await fetch(`${API_URL}/api/google-docs/embed/${file.id}?user_id=${encodeURIComponent(userId)}&mode=${mode}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedGoogleDoc({
          ...file,
          embed_url: data.embed_url
        });
        setGoogleViewMode(mode);
        setShowGoogleDocViewer(true);
      }
    } catch (error) {
      toast.error('Failed to open document');
    }
  };

  const createGoogleDoc = async () => {
    if (!newGoogleDocName.trim()) {
      toast.error('Please enter a document name');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/google-docs/create?user_id=${encodeURIComponent(userId)}&name=${encodeURIComponent(newGoogleDocName)}&file_type=${newGoogleDocType}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${newGoogleDocType === 'doc' ? 'Document' : 'Spreadsheet'} created!`);
        setShowCreateGoogleDocModal(false);
        setNewGoogleDocName('');
        fetchGoogleFiles(googleFolderId);
        window.open(data.file.web_view_link, '_blank');
      } else {
        toast.error('Failed to create document');
      }
    } catch (error) {
      toast.error('Failed to create document');
    }
  };

  return (
    <div className="mt-6">
      {!googleConnected ? (
        <div className="text-center py-16 bg-gray-900 rounded-lg border border-gray-800">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <GoogleDocsIcon size={64} />
          </div>
          <h3 className="text-xl font-bold mb-2">Connect Google Docs & Sheets</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Browse, view, and edit your Google Docs and Sheets directly from your dashboard.
          </p>
          <button
            onClick={connectGoogleDocs}
            data-testid="connect-google-docs-btn"
            className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium flex items-center gap-2 mx-auto"
          >
            <Link size={18} />
            Connect Google Account
          </button>
        </div>
      ) : (
        <>
          {/* Connected Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <GoogleDocsIcon size={24} />
              </div>
              <div>
                <h3 className="font-semibold">Google Docs Connected</h3>
                <p className="text-sm text-gray-400">{googleEmail || 'Connected'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {googleFolderHistory.length > 0 && (
                <button
                  onClick={navigateGoogleBack}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2 text-sm"
                >
                  <ChevronDown size={16} className="rotate-90" />
                  Back
                </button>
              )}
              <button
                onClick={() => setShowCreateGoogleDocModal(true)}
                className="px-3 py-2 bg-green-600 hover:bg-green-500 rounded-lg flex items-center gap-2 text-sm"
              >
                <Plus size={16} />
                New
              </button>
              <button
                onClick={() => fetchGoogleFiles(googleFolderId)}
                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
                title="Refresh"
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={disconnectGoogleDocs}
                className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg flex items-center gap-2 text-sm"
              >
                <Unlink size={16} />
                Disconnect
              </button>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-800">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === cat.id
                    ? `${cat.color} text-white`
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${cat.color}`}></span>
                {cat.label}
              </button>
            ))}
          </div>

          {/* File Type Filter */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setGoogleFileType('all')}
              className={`px-3 py-1.5 rounded-lg text-sm ${googleFileType === 'all' ? 'bg-purple-600' : 'bg-gray-800 hover:bg-gray-700'}`}
            >
              All Files
            </button>
            <button
              onClick={() => setGoogleFileType('docs')}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 ${googleFileType === 'docs' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'}`}
            >
              <GoogleDocsIcon size={14} />
              Docs Only
            </button>
            <button
              onClick={() => setGoogleFileType('sheets')}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 ${googleFileType === 'sheets' ? 'bg-green-600' : 'bg-gray-800 hover:bg-gray-700'}`}
            >
              <GoogleSheetsIcon size={14} />
              Sheets Only
            </button>
          </div>

          {/* Files Grid */}
          {googleLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-blue-400" />
            </div>
          ) : googleFiles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {googleFiles.map((file) => (
                <div
                  key={file.id}
                  className="bg-gray-900 rounded-lg border border-gray-800 hover:border-blue-500/50 transition-all overflow-hidden cursor-pointer group"
                  onClick={() => file.type === 'folder' ? navigateGoogleFolder(file) : openGoogleDoc(file, 'view')}
                >
                  <div className="aspect-[4/3] bg-gray-800 flex items-center justify-center relative">
                    {file.type === 'folder' ? (
                      <FolderOpen size={48} className="text-yellow-400" />
                    ) : file.thumbnail_url ? (
                      <img src={file.thumbnail_url} alt={file.name} className="w-full h-full object-cover" />
                    ) : file.file_type === 'doc' ? (
                      <GoogleDocsIcon size={48} />
                    ) : (
                      <GoogleSheetsIcon size={48} />
                    )}
                    
                    {file.type !== 'folder' && (
                      <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${
                        file.file_type === 'doc' ? 'bg-blue-600' : 'bg-green-600'
                      }`}>
                        {file.file_type === 'doc' ? 'DOC' : 'SHEET'}
                      </div>
                    )}

                    {file.type !== 'folder' && (
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); openGoogleDoc(file, 'view'); }}
                          className="p-2 bg-blue-500 rounded-full hover:bg-blue-600"
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openGoogleDoc(file, 'edit'); }}
                          className="p-2 bg-green-500 rounded-full hover:bg-green-600"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); window.open(file.web_view_link, '_blank'); }}
                          className="p-2 bg-purple-500 rounded-full hover:bg-purple-600"
                          title="Open in New Tab"
                        >
                          <ExternalLink size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-sm truncate">{file.name}</h3>
                    <p className="text-xs text-gray-400 mt-1">
                      {file.modified ? new Date(file.modified).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-900/50 rounded-lg border border-gray-800">
              <FileText size={48} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">No documents found</p>
              <button
                onClick={() => setShowCreateGoogleDocModal(true)}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm"
              >
                Create New Document
              </button>
            </div>
          )}
        </>
      )}

      {/* Google Doc Viewer Modal */}
      {showGoogleDocViewer && selectedGoogleDoc && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gray-900">
            <div className="flex items-center gap-3">
              {selectedGoogleDoc.file_type === 'doc' ? <GoogleDocsIcon size={24} /> : <GoogleSheetsIcon size={24} />}
              <div>
                <h3 className="font-semibold">{selectedGoogleDoc.name}</h3>
                <p className="text-xs text-gray-400">{googleViewMode === 'edit' ? 'Editing' : 'Viewing'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setGoogleViewMode(googleViewMode === 'view' ? 'edit' : 'view')}
                className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 ${
                  googleViewMode === 'edit' ? 'bg-green-600' : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                {googleViewMode === 'edit' ? <Eye size={14} /> : <Edit2 size={14} />}
                {googleViewMode === 'edit' ? 'Switch to View' : 'Switch to Edit'}
              </button>
              <button
                onClick={() => window.open(selectedGoogleDoc.web_view_link, '_blank')}
                className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm flex items-center gap-2"
              >
                <ExternalLink size={14} />
                Open in Google
              </button>
              <button
                onClick={() => { setShowGoogleDocViewer(false); setSelectedGoogleDoc(null); }}
                className="p-2 hover:bg-gray-800 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="flex-1 bg-white">
            <iframe
              src={selectedGoogleDoc.embed_url}
              className="w-full h-full border-0"
              allow="clipboard-read; clipboard-write"
              title={selectedGoogleDoc.name}
            />
          </div>
        </div>
      )}

      {/* Create Google Doc Modal */}
      {showCreateGoogleDocModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" onClick={() => setShowCreateGoogleDocModal(false)}>
          <div className="bg-gray-900 rounded-xl max-w-md w-full p-6 border border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Create New Document</h3>
              <button onClick={() => setShowCreateGoogleDocModal(false)} className="p-1 hover:bg-gray-800 rounded">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Document Name</label>
                <input
                  type="text"
                  value={newGoogleDocName}
                  onChange={(e) => setNewGoogleDocName(e.target.value)}
                  placeholder="Enter document name..."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Document Type</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewGoogleDocType('doc')}
                    className={`flex-1 p-4 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                      newGoogleDocType === 'doc' ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <GoogleDocsIcon size={32} />
                    <span className="text-sm font-medium">Google Doc</span>
                  </button>
                  <button
                    onClick={() => setNewGoogleDocType('sheet')}
                    className={`flex-1 p-4 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                      newGoogleDocType === 'sheet' ? 'border-green-500 bg-green-500/10' : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <GoogleSheetsIcon size={32} />
                    <span className="text-sm font-medium">Google Sheet</span>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowCreateGoogleDocModal(false)}
                className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={createGoogleDoc}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { GoogleDocsSection, GoogleDocsIcon, GoogleSheetsIcon };
export default GoogleDocsSection;
